// services/advances.js — авансовите фактури и приспадането им (Етап 3).
//
// Авансовата фактура е обикновена фактура с отметка advance: при
// записванията за Конто редовете ѝ отиват по Кт 412, не по приход. В
// окончателната авансът се приспада с ОТРИЦАТЕЛЕН ред, който сочи авансовата
// (advance_of): computeTotals го смята като всеки ред — основата и ДДС-ът са
// след приспадането, точно както ги иска дневникът (NET_VALUE на 4532), а
// PDF-ът го печата като ред „Приспадане на аванс по ф-ра № … от …". При
// записванията редът става Кт 412 с минус, с № и дата на АВАНСОВАТА —
// по образеца на Дазъл (ОТГОВОР-ПРОМТ-20).
//
// ПРОМТ-44 (30.10.2026): същите правила важат и за аванса към ДОСТАВЧИК
// (services/purchaseAdvances.js) — доставките имат други имена на полетата
// (lines вместо items, ref_doc_id, date, стойността на реда е subtotal с
// отстъпката). Затова всяка функция приема вид (KINDS) — логиката е една.
//
// Чисти функции — ползват ги сървърът (проверката) и екранът (менюто).

const { round2 } = require('./totals');

function lineValue(it) { return (Number(it && it.quantity) || 0) * (Number(it && it.unit_price) || 0); }

// Полетата и думите по вид документ. invoices е подразбирането — така
// заварените извиквания (фактурите) не се променят.
const KINDS = {
  invoices: {
    lines: 'items', ref: 'ref_invoice_id', date: 'issue_date', value: lineValue,
    party: 'клиент', doc: 'фактура', final: 'обикновена (окончателна) фактура', self: 'Фактурата не може да приспада сама себе си.',
  },
  purchases: {
    lines: 'lines', ref: 'ref_doc_id', date: 'date',
    // Редът на доставката носи стойността си (с отстъпката) — services/purchaseItems.js.
    value: (l) => (l && l.subtotal != null ? Number(l.subtotal) || 0 : lineValue(l)),
    party: 'доставчик', doc: 'доставка', final: 'обикновена (окончателна) фактура от доставчика', self: 'Доставката не може да приспада сама себе си.',
  },
};
function kindOf(kind) { return KINDS[kind] || KINDS.invoices; }
function linesOf(doc, k) { const x = doc && doc[k.lines]; return Array.isArray(x) ? x : []; }
function hasAdvanceLine(doc, kind) {
  return linesOf(doc, kindOf(kind)).some((it) => it && it.advance_of != null && it.advance_of !== '');
}

// Колко е приспаднато от аванса в ДРУГИ документи (без exceptId) —
// положително число.
function deducted(invoices, advanceId, exceptId, kind) {
  const k = kindOf(kind);
  let sum = 0;
  for (const inv of (Array.isArray(invoices) ? invoices : [])) {
    if (Number(inv.id) === Number(exceptId)) continue;
    for (const it of linesOf(inv, k)) {
      if (Number(it && it.advance_of) === Number(advanceId)) sum += -k.value(it);
    }
  }
  return round2(sum);
}

// Остатъкът за приспадане: основата на аванса, намалена с кредитните
// известия към него и с приспаднатото другаде.
function available(invoices, adv, exceptId, kind) {
  const k = kindOf(kind);
  let base = Number(adv && adv.subtotal) || 0;
  for (const inv of (Array.isArray(invoices) ? invoices : [])) {
    if (inv.doc_type === 'credit' && Number(inv[k.ref]) === Number(adv.id)) base += Number(inv.subtotal) || 0;
  }
  return round2(base - deducted(invoices, adv.id, exceptId, kind));
}

// Авансите на клиента (доставчика) с остатък — за менюто „Приспадни аванс".
// Връща { invoice, available } — invoice е авансовият документ и при доставките.
function openAdvances(invoices, contactId, exceptId, kind) {
  const k = kindOf(kind);
  return (Array.isArray(invoices) ? invoices : [])
    .filter((i) => i.advance && Number(i.contact_id) === Number(contactId) && Number(i.id) !== Number(exceptId) && (i.doc_type || 'invoice') === 'invoice')
    .map((i) => ({ invoice: i, available: available(invoices, i, exceptId, kind) }))
    .filter((x) => x.available > 0)
    .sort((a, b) => String(a.invoice[k.date]).localeCompare(String(b.invoice[k.date])));
}

// Проверката преди запис. data: документът (с items/lines, contact_id, advance,
// doc_type, vat_rate, id ако е заварен); invoices: документите на фирмата.
// Връща текста на отказа или null.
function checkDeductions(data, invoices, kind) {
  const k = kindOf(kind);
  const byId = new Map((Array.isArray(invoices) ? invoices : []).map((i) => [Number(i.id), i]));
  const per = new Map();
  for (const it of linesOf(data, k)) {
    if (it.advance_of == null) continue;
    // Всяко копие на повтаряща се фактура (generate-due) би приспаднало
    // същия аванс пак — без проверка, защото копието не минава оттук.
    if (data.recurring) return 'Повтаряща се фактура не може да приспада аванс — всяко ново копие би го приспаднало пак.';
    const adv = byId.get(Number(it.advance_of));
    if (!adv || !adv.advance) return 'Приспаднатият аванс сочи ' + k.doc + ', която не е авансова.';
    if (Number(adv.id) === Number(data.id)) return k.self;
    if ((data.doc_type || 'invoice') !== 'invoice' || data.advance) return 'Аванс се приспада само в ' + k.final + '.';
    if (Number(adv.contact_id) !== Number(data.contact_id)) return 'Авансът № ' + adv.number + ' е на друг ' + k.party + '.';
    if (k.value(it) >= 0) return 'Редът с приспаднатия аванс трябва да е с минус.';
    const rate = it.vat_rate != null ? Number(it.vat_rate) : Number(data.vat_rate);
    if (rate !== Number(adv.vat_rate)) return 'Авансът № ' + adv.number + ' е с ДДС ' + adv.vat_rate + ' % — приспада се със същата ставка.';
    per.set(Number(adv.id), round2((per.get(Number(adv.id)) || 0) - k.value(it)));
  }
  for (const [advId, amount] of per) {
    const adv = byId.get(advId);
    const left = available(invoices, adv, data.id, kind);
    if (amount > left + 0.001) return 'От аванс № ' + adv.number + ' остават ' + left.toFixed(2) + ' — не може да се приспаднат ' + amount.toFixed(2) + '.';
  }
  return null;
}

module.exports = { KINDS, deducted, available, openAdvances, checkDeductions, hasAdvanceLine };
